import React, { useEffect, useRef, useState } from "react";
import { api } from "../../../software/api";
import { toast } from "sonner";
import { X, Calculator } from "lucide-react";
import {
  CATEGORIES, catLabel, WB_TYPES, SURFACES, FILTERS, WATER_SOURCES,
  SALT_LEVELS, MAG_LEVELS, CHEM_BRANDS, PHONE_TYPES,
} from "./waterChem";
import VolumeCalculator from "./VolumeCalculator";

const AU_STATES = ["NSW", "QLD", "VIC", "SA", "WA", "TAS", "NT", "ACT"];

const fieldCls = "w-full rounded-md bg-white px-3 py-1.5 text-[14px] text-slate-800 placeholder-slate-400 outline-none focus:ring-2 focus:ring-[#8bc53f]/60";

// Defined at module scope so inputs are NOT remounted on every keystroke (kept focus).
const Row = ({ label, children, accent }) => (
  <div className="grid grid-cols-[120px_1fr] items-center gap-3 mb-2">
    <label className={`text-right text-[13px] font-black ${accent || "text-white"}`}>{label}</label>
    <div className="relative">{children}</div>
  </div>
);
const Sel = ({ value, onChange, options, placeholder, testid }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} className={fieldCls} data-testid={testid}>
    {placeholder && <option value="">{placeholder}</option>}
    {options.map((o) => (Array.isArray(o) ? <option key={o[0]} value={o[0]}>{o[1]}</option> : <option key={o} value={o}>{o}</option>))}
  </select>
);
const Check = ({ checked, onChange, label, accent, testid }) => (
  <label className="flex items-center justify-end gap-2 text-[12px] font-black text-white cursor-pointer select-none">
    <span className={accent || ""}>{label}:</span>
    <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} data-testid={testid} className="h-4 w-4 accent-[#8bc53f]" />
  </label>
);

export default function AddNewClient({ onSubmit, onClose }) {
  const [f, setF] = useState({
    lead_source: "", first_name: "", surname: "", street_number: "", street_name: "",
    suburb: "", state: "NSW", postcode: "", company: "",
    phone_type: "Mobile", phone: "", email: "",
    bow: "", bow_type: "", surface: "", filter_type: "", water_source: "", chemical_brand: "",
    volume: "", salt_level: "", magnesium_level: "",
    leafy: false, covered: false, inground: false, above_ground: false, residential: false, commercial: false,
    liquid_acid: false, powder_acid: false, loyalty_number: "",
  });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const [leadSources, setLeadSources] = useState([]);
  const [subMatches, setSubMatches] = useState([]);
  const [showSub, setShowSub] = useState(false);
  const [showCalc, setShowCalc] = useState(false);
  const [busy, setBusy] = useState(false);
  const subTimer = useRef(null);

  useEffect(() => { api("GET", "/lookups/lead-sources").then(setLeadSources).catch(() => setLeadSources([])); }, []);

  const onSuburb = (v) => {
    set("suburb", v);
    clearTimeout(subTimer.current);
    if (v.trim().length < 2) { setSubMatches([]); setShowSub(false); return; }
    subTimer.current = setTimeout(() => {
      api("GET", `/lookups/suburbs?q=${encodeURIComponent(v.trim())}`).then((r) => { setSubMatches(r || []); setShowSub(true); }).catch(() => setSubMatches([]));
    }, 220);
  };
  const pickSuburb = (m) => { setF((s) => ({ ...s, suburb: m.suburb, state: m.state || s.state, postcode: m.postcode || s.postcode })); setShowSub(false); };

  const build = (action) => {
    const name = `${f.first_name} ${f.surname}`.trim();
    if (!name) { toast.error("Enter a first name or surname"); return null; }
    const details = [];
    if (f.phone) details.push({ type: f.phone_type === "Mobile" ? "Mobile Phone" : f.phone_type, value: f.phone });
    if (f.email) details.push({ type: "Email", value: f.email });
    const contacts = (f.first_name || f.surname || details.length)
      ? [{ first_name: f.first_name, surname: f.surname, position: "", company: f.company || "", relationship: "", details, mobile: f.phone || "", email: f.email || "", billing: false }]
      : [];
    const clientBody = {
      name, email: f.email || "", phone: f.phone || "", emails: f.email ? [f.email] : [], phones: f.phone ? [f.phone] : [],
      company: f.company || "", status: action === "spa_lead" ? "lead" : "active",
      lead_source: f.lead_source || "", loyalty_number: (f.loyalty_number || "").trim(), trade_discount: 0,
      address: { street: [f.street_number, f.street_name].filter(Boolean).join(" "), suburb: f.suburb || "", state: f.state || "", postcode: f.postcode || "" },
      contacts, notes: "", pool: {},
      notify_email: true, notify_sms: true, do_not_contact: false,
      regarding: "", participant_number: "", service_code: "", approval_number: "",
    };
    // only create a body of water when one was chosen
    const bowDetails = [f.bow_type, f.surface, f.volume, f.filter_type, f.water_source, f.salt_level, f.magnesium_level, f.chemical_brand].some(Boolean)
      || [f.leafy, f.covered, f.inground, f.above_ground, f.residential, f.commercial, f.liquid_acid, f.powder_acid].some(Boolean);
    if (!f.bow && bowDetails) { toast.error("Choose the Body of Water (Pool, Spa or Swim Spa) for the water details you entered"); return null; }
    const waterBody = f.bow && {
      name: catLabel(f.bow), category: f.bow, type: f.bow_type || "", surface: f.surface || "",
      volume_litres: parseInt(f.volume) || 0, filter_type: f.filter_type || "", water_source: f.water_source || "",
      salt_level: f.salt_level || "", magnesium_level: f.magnesium_level || "", chemical_brand: f.chemical_brand || "",
      covered: !!f.covered, leafy: !!f.leafy, inground: !!f.inground, above_ground: !!f.above_ground,
      residential: !!f.residential, commercial: !!f.commercial, liquid_acid: !!f.liquid_acid, powder_acid: !!f.powder_acid,
    };
    return { clientBody, waterBody, loyalty: (f.loyalty_number || "").trim() };
  };

  const go = async (action) => {
    if (busy) return;
    const data = build(action);
    if (!data) return;
    setBusy(true);
    try { await onSubmit(action, data); } finally { setBusy(false); }
  };

  // --- render ---
  return (
    <div className="fixed inset-0 z-[70] flex items-start justify-center p-3 overflow-auto bg-black/60 backdrop-blur-sm" data-testid="add-new-client-modal" onClick={onClose}>
      <div className="relative my-6 w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
        {/* assistant figure (overlaps card on the left) */}
        <img src="/add-client-assistant.png" alt="" className="hidden lg:block absolute -left-24 bottom-0 w-56 pointer-events-none select-none drop-shadow-2xl" style={{ zIndex: 2 }} />
        <div className="relative rounded-[28px] bg-[#232a3d] border border-[#3a4257] shadow-2xl px-6 py-5 lg:pl-40">
          <button onClick={onClose} data-testid="anc-close" className="absolute top-4 right-5 text-white/70 hover:text-white"><X className="h-6 w-6" /></button>
          <h1 className="text-center font-display font-black tracking-wide text-[34px] leading-none text-[#8bc53f] mb-4" data-testid="anc-title">+ ADD NEW CLIENT</h1>

          {/* ---- top: contact details ---- */}
          <div className="max-w-2xl mx-auto">
            <Row label="Lead Source:"><Sel value={f.lead_source} onChange={(v) => set("lead_source", v)} options={leadSources} placeholder="Select lead source…" testid="anc-lead_source" /></Row>
            <Row label="First Name:"><input value={f.first_name} onChange={(e) => set("first_name", e.target.value)} className={fieldCls} data-testid="anc-first_name" /></Row>
            <Row label="Surname:"><input value={f.surname} onChange={(e) => set("surname", e.target.value)} className={fieldCls} data-testid="anc-surname" /></Row>
            <div className="grid grid-cols-[120px_90px_1fr] items-center gap-3 mb-2">
              <label className="text-right text-[13px] font-black text-white">Street No:</label>
              <input value={f.street_number} onChange={(e) => set("street_number", e.target.value)} className={fieldCls} data-testid="anc-street_number" />
              <input value={f.street_name} onChange={(e) => set("street_name", e.target.value)} placeholder="Street Name" className={fieldCls} data-testid="anc-street_name" />
            </div>
            <div className="grid grid-cols-[120px_1fr_auto_90px_auto_90px] items-center gap-2 mb-2">
              <label className="text-right text-[13px] font-black text-white">Suburb:</label>
              <div className="relative">
                <input value={f.suburb} onChange={(e) => onSuburb(e.target.value)} onFocus={() => subMatches.length && setShowSub(true)} onBlur={() => setTimeout(() => setShowSub(false), 200)} className={fieldCls} data-testid="anc-suburb" autoComplete="off" />
                {showSub && subMatches.length > 0 && (
                  <div className="absolute z-30 mt-0.5 w-full max-h-48 overflow-auto rounded-md bg-white border border-slate-300 shadow-xl" data-testid="anc-suburb-list">
                    {subMatches.map((m, i) => (
                      <button key={i} type="button" onClick={() => pickSuburb(m)} data-testid={`anc-suburb-opt-${i}`} className="block w-full text-left px-3 py-1.5 text-[13px] text-slate-700 hover:bg-[#eef3fb]">{m.suburb} <span className="text-slate-400">· {m.state} {m.postcode}</span></button>
                    ))}
                  </div>
                )}
              </div>
              <label className="text-[12px] font-black text-white">State</label>
              <select value={f.state} onChange={(e) => set("state", e.target.value)} className={fieldCls} data-testid="anc-state">{AU_STATES.map((s) => <option key={s}>{s}</option>)}</select>
              <label className="text-[12px] font-black text-white text-right">P/Code</label>
              <input value={f.postcode} onChange={(e) => set("postcode", e.target.value)} className={fieldCls} data-testid="anc-postcode" />
            </div>
            <Row label="Company:"><input value={f.company} onChange={(e) => set("company", e.target.value)} className={fieldCls} data-testid="anc-company" /></Row>
            <div className="grid grid-cols-[120px_150px_1fr] items-center gap-3 mb-2">
              <label className="text-right text-[13px] font-black text-white">Phone:</label>
              <select value={f.phone_type} onChange={(e) => set("phone_type", e.target.value)} className={fieldCls} data-testid="anc-phone_type">{PHONE_TYPES.map((p) => <option key={p}>{p}</option>)}</select>
              <input value={f.phone} onChange={(e) => set("phone", e.target.value)} className={fieldCls} data-testid="anc-phone" />
            </div>
            <Row label="Email:"><input type="email" value={f.email} onChange={(e) => set("email", e.target.value)} className={fieldCls} data-testid="anc-email" /></Row>
          </div>

          {/* ---- action buttons ---- */}
          <div className="flex flex-wrap justify-center gap-3 my-4">
            <button onClick={() => go("work_order")} disabled={busy} data-testid="anc-new-work-order" className="px-5 py-2 rounded-md bg-[#1a7a1f] hover:bg-[#166618] text-white font-black text-[14px] shadow-lg disabled:opacity-60">New Work Order</button>
            <button onClick={() => go("spa_lead")} disabled={busy} data-testid="anc-new-spa-lead" className="px-5 py-2 rounded-md bg-[#7a1f8a] hover:bg-[#661673] text-white font-black text-[14px] shadow-lg disabled:opacity-60">New Spa Lead</button>
            <button onClick={() => go("maytronics")} disabled={busy} data-testid="anc-maytronics" className="px-5 py-2 rounded-md bg-[#c8a12a] hover:bg-[#b08f20] text-white font-black text-[14px] shadow-lg disabled:opacity-60">Maytronics E-Sale</button>
          </div>

          {/* ---- body of water ---- */}
          <div className="grid md:grid-cols-2 gap-x-8 gap-y-0">
            <div>
              <Row label="Body of Water:"><Sel value={f.bow} onChange={(v) => set("bow", v)} options={CATEGORIES} placeholder="None — no body of water" testid="anc-bow" /></Row>
              <Row label="Water Type:"><Sel value={f.bow_type} onChange={(v) => set("bow_type", v)} options={WB_TYPES} placeholder="Select…" testid="anc-bow_type" /></Row>
              <Row label="Surface Type:"><Sel value={f.surface} onChange={(v) => set("surface", v)} options={SURFACES} placeholder="Select…" testid="anc-surface" /></Row>
              <Row label="Filter Type:"><Sel value={f.filter_type} onChange={(v) => set("filter_type", v)} options={FILTERS} placeholder="Select…" testid="anc-filter_type" /></Row>
              <Row label="Water Source:"><Sel value={f.water_source} onChange={(v) => set("water_source", v)} options={WATER_SOURCES} placeholder="Select…" testid="anc-water_source" /></Row>
              <Row label="Chemical:"><Sel value={f.chemical_brand} onChange={(v) => set("chemical_brand", v)} options={CHEM_BRANDS} placeholder="Select…" testid="anc-chemical_brand" /></Row>
            </div>
            <div>
              <div className="grid grid-cols-[130px_1fr_auto] items-center gap-2 mb-2">
                <label className="text-right text-[13px] font-black text-white">Volume (Litres):</label>
                <input value={f.volume} onChange={(e) => set("volume", e.target.value)} inputMode="numeric" placeholder="35000" className={fieldCls} data-testid="anc-volume" />
                <button onClick={() => setShowCalc(true)} data-testid="anc-open-calculator" title="Pool Water Volume Calculator" className="rounded-md bg-[#b7e3f0] hover:bg-[#9ad6e8] border border-[#7ba7d9] p-1.5"><Calculator className="h-6 w-6 text-[#0e5a73]" /></button>
              </div>
              <Row label="Salt Level:"><Sel value={f.salt_level} onChange={(v) => set("salt_level", v)} options={SALT_LEVELS} placeholder="If applicable…" testid="anc-salt_level" /></Row>
              <Row label="Magnesium / TDS:"><Sel value={f.magnesium_level} onChange={(v) => set("magnesium_level", v)} options={MAG_LEVELS} placeholder="If applicable…" testid="anc-magnesium_level" /></Row>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 mt-2">
                <Check checked={f.leafy} onChange={(v) => set("leafy", v)} label="Leafy Area" accent="text-[#8bc53f]" testid="anc-leafy" />
                <Check checked={f.covered} onChange={(v) => set("covered", v)} label="Has Cover" testid="anc-covered" />
                <Check checked={f.inground} onChange={(v) => set("inground", v)} label="Inground" testid="anc-inground" />
                <Check checked={f.above_ground} onChange={(v) => set("above_ground", v)} label="Above Ground" testid="anc-above_ground" />
                <Check checked={f.residential} onChange={(v) => set("residential", v)} label="Residential" testid="anc-residential" />
                <Check checked={f.commercial} onChange={(v) => set("commercial", v)} label="Commercial" testid="anc-commercial" />
                <Check checked={f.liquid_acid} onChange={(v) => set("liquid_acid", v)} label="Liquid Acid" testid="anc-liquid_acid" />
                <Check checked={f.powder_acid} onChange={(v) => set("powder_acid", v)} label="Powder Acid" testid="anc-powder_acid" />
              </div>
            </div>
          </div>

          {/* ---- loyalty + water test ---- */}
          <div className="grid grid-cols-[120px_1fr] items-center gap-3 mt-4 max-w-2xl mx-auto">
            <label className="text-right text-[14px] font-black text-[#f4d03f]">Loyalty No:</label>
            <div className="flex items-center gap-3">
              <input value={f.loyalty_number} onChange={(e) => set("loyalty_number", e.target.value)} className={`${fieldCls} w-40`} data-testid="anc-loyalty_number" />
              <span className="text-[11px] font-bold text-[#f4d03f]">(Give client a loyalty number and place number on water bottle)</span>
            </div>
          </div>

          <div className="flex justify-center mt-4">
            <button onClick={() => go("water_test")} disabled={busy} data-testid="anc-new-water-test" className="px-8 py-2.5 rounded-md bg-[#2f5fae] hover:bg-[#26509a] text-white font-black text-[15px] shadow-lg disabled:opacity-60">New Water Test</button>
          </div>
        </div>
      </div>

      {showCalc && <VolumeCalculator initialLitres={parseInt(f.volume) || 0} onUse={(l) => set("volume", String(l))} onClose={() => setShowCalc(false)} />}
    </div>
  );
}
